import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { UserPreferences } from "@shared/schema";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";

export interface TaskFilters {
  filterTab?: string;
  completionFilter?: string;
  categoryFilter?: string[];  // Changed to array for multi-select
  assignedToFilter?: "all" | "me" | "others" | "sharedWithMe";
  selectedAssigneeNames?: string[];
  searchQuery?: string;
  dateRangeStart?: string;  // Date range filter - start date
  dateRangeEnd?: string;    // Date range filter - end date
  showOnlyLatestRecurring?: boolean;  // Show only most recent recurring task per group
  showTopThreeOnly?: boolean;  // Show only starred/top three tasks for the day
  sortBy1?: "none" | "displayOrder" | "dueDate" | "createdAt" | "priority" | "assignedToOther";
  sortOrder1?: "asc" | "desc";
  sortBy2?: "none" | "displayOrder" | "dueDate" | "createdAt" | "priority" | "assignedToOther";
  sortOrder2?: "asc" | "desc";
  sortBy3?: "none" | "displayOrder" | "dueDate" | "createdAt" | "priority" | "assignedToOther";
  sortOrder3?: "asc" | "desc";
  groupBy1?: string;
  groupBy2?: string;
  groupBy3?: string;
  viewMode?: "list" | "kanban";
  filtersExpanded?: boolean;
  // Assigned to Others tab settings
  assignedToOthersFilter?: "all" | "active" | "completed";
  assignedToOthersSortBy?: "dueDate" | "title" | "assignee";
  assignedToOthersSortOrder?: "asc" | "desc";
  assignedToOthersGroupBy?: "none" | "assignee" | "status";
  showSubtasks?: boolean;
  hideCompletedSubtasks?: boolean;
  perTaskHideCompleted?: string[];
}

const STORAGE_KEY = "tasksFilterPreferences";

export function useTaskFilters(defaultFilters: TaskFilters = {}) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [filters, setFilters] = useState<TaskFilters>(defaultFilters);
  const initializedRef = useRef(false);
  const isInitializingRef = useRef(true); // Track if we're still initializing

  // Fetch preferences from backend
  const { data: preferences, isLoading } = useQuery<UserPreferences>({
    queryKey: ["/api/user/preferences"],
  });

  // Mutation to save preferences to backend
  // Destructure stable mutate function to avoid stale closures
  const { mutate: saveTaskFilters } = useMutation({
    mutationFn: async (taskFilters: TaskFilters) => {
      console.log("[useTaskFilters] Saving taskFilters to backend:", taskFilters);
      return await apiRequest("PATCH", "/api/user/preferences", { taskFilters });
    },
    onSuccess: (data) => {
      console.log("[useTaskFilters] Backend returned:", data);
      // Immediately update the query cache to prevent resets
      queryClient.setQueryData(["/api/user/preferences"], data);
    },
    // Don't invalidate here - it causes infinite loop since preferences is in useEffect deps
  });

  // Initialize filters from backend preferences or localStorage (only once)
  useEffect(() => {
    // Wait until preferences query is complete (not loading)
    if (isLoading || initializedRef.current) return;

    // Mark as initialized ONLY after we've checked preferences
    initializedRef.current = true;

    try {
      let storedFilters: TaskFilters | undefined;

      // Try to get from backend first (preferences will be defined if query succeeded)
      if (preferences?.taskFilters) {
        console.log("[useTaskFilters] Loading filters from backend:", preferences.taskFilters);
        storedFilters = preferences.taskFilters;
      } else {
        // Fallback to localStorage only if backend has no data
        const stored = localStorage.getItem(userLsKey(STORAGE_KEY, userId));
        if (stored) {
          console.log("[useTaskFilters] Loading filters from localStorage");
          storedFilters = JSON.parse(stored);
        } else {
          console.log("[useTaskFilters] No stored filters found, using defaults");
        }
      }

      if (storedFilters) {
        console.log("[useTaskFilters] RAW stored filters from localStorage:", storedFilters);
        
        // Normalize legacy categoryFilter (string → array) for backward compatibility
        if (storedFilters.categoryFilter) {
          if (typeof storedFilters.categoryFilter === 'string') {
            // Convert legacy string values to arrays
            if (storedFilters.categoryFilter === 'all') {
              storedFilters.categoryFilter = [];  // "all" → empty array (show all)
            } else {
              storedFilters.categoryFilter = [storedFilters.categoryFilter];  // "Work" → ["Work"]
            }
          }
        }
        
        // CRITICAL BUG FIX: ALWAYS delete showOnlyLatestRecurring from stored filters
        // This filter was causing all tasks to disappear in production
        if ('showOnlyLatestRecurring' in storedFilters) {
          console.log("[useTaskFilters] REMOVING showOnlyLatestRecurring from stored filters - was:", storedFilters.showOnlyLatestRecurring);
          delete storedFilters.showOnlyLatestRecurring;
          // Save the corrected filters immediately
          saveTaskFilters(storedFilters);
        }
        
        console.log("[useTaskFilters] CLEANED stored filters:", storedFilters);
        setFilters({ ...defaultFilters, ...storedFilters });
      }
      
      // Mark initialization as complete AFTER setting filters
      setTimeout(() => {
        isInitializingRef.current = false;
        console.log("[useTaskFilters] Initialization complete");
      }, 0);
    } catch (error) {
      console.error("Failed to load task filters:", error);
      isInitializingRef.current = false;
    }
  }, [preferences, isLoading]);

  const updateFilters = useCallback((newFilters: Partial<TaskFilters>) => {
    setFilters((currentFilters) => {
      const updated = { ...currentFilters, ...newFilters };
      
      // Normalize categoryFilter to always be an array
      if (updated.categoryFilter && typeof updated.categoryFilter === 'string') {
        if (updated.categoryFilter === 'all') {
          updated.categoryFilter = [];
        } else {
          updated.categoryFilter = [updated.categoryFilter as any];
        }
      }
      
      // Only save to backend if initialization is complete
      if (isInitializingRef.current) {
        console.log("[useTaskFilters] Skipping backend save during initialization");
        return updated;
      }
      
      try {
        // Save to localStorage immediately for instant persistence
        localStorage.setItem(userLsKey(STORAGE_KEY, userId), JSON.stringify(updated));
        
        // Save to backend (this will be async)
        saveTaskFilters(updated);
      } catch (error) {
        console.error("Failed to save task filters:", error);
      }
      
      return updated;
    });
  }, [saveTaskFilters]);

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
    try {
      // Clear backend preference
      saveTaskFilters({});
      
      // Clear localStorage
      localStorage.removeItem(userLsKey(STORAGE_KEY, userId));
    } catch (error) {
      console.error("Failed to reset task filters:", error);
    }
  }, [defaultFilters, saveTaskFilters]);

  return { filters, updateFilters, resetFilters, isLoading };
}
